/**
 * QR Code Scanner Component
 *
 * Uses qr-scanner (nimiq) for camera and image scanning.
 * Supports: live camera, image file upload, manual entry.
 */

import React, { useState, useRef, useEffect } from 'react';
import QrScanner from 'qr-scanner';
import jsQR from 'jsqr';

export interface QRScannerLabels {
  camera?: string;
  upload_image?: string;
  manual_entry?: string;
  start_camera?: string;
  stop_scanning?: string;
  camera_hint?: string;
  upload_hint?: string;
  manual_placeholder?: string;
  process_qr_data?: string;
  manual_hint_example?: string;
  no_qr_in_image?: string;
  enter_qr_data?: string;
  scan_current_frame?: string;
  processing?: string;
}

export type QRScannerMode = 'camera' | 'upload' | 'manual';

interface QRScannerProps {
  onScanSuccess: (qrData: string) => void;
  onScanError?: (error: string) => void;
  width?: number;
  height?: number;
  labels?: QRScannerLabels;
  /** When set, only these modes are shown. If one mode, the mode selector is hidden. */
  modes?: QRScannerMode[];
}

const DEFAULT_LABELS: Required<QRScannerLabels> = {
  camera: 'Camera',
  upload_image: 'Upload Image',
  manual_entry: 'Manual Entry',
  start_camera: 'Start Camera Scanning',
  stop_scanning: 'Stop Scanning',
  camera_hint: "Point the camera at the receipt QR and hold it steady for about a second. If it doesn't detect, try Upload Image or Manual Entry.",
  upload_hint: 'Select an image containing a QR code',
  manual_placeholder: 'Paste QR code data here (JSON or pipe-delimited format)',
  process_qr_data: 'Process QR Data',
  manual_hint_example: 'Example formats:',
  no_qr_in_image: 'No QR code found in this image. Try a clearer photo, ensure the whole code is visible, or use Manual Entry.',
  enter_qr_data: 'Please enter QR code data',
  scan_current_frame: 'Scan current frame',
  processing: 'Processing…',
};

/** Map camera/getUserMedia errors to user-friendly messages */
function getCameraErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  const name = err instanceof Error ? (err as Error & { name?: string }).name : '';
  if (msg.includes('NotAllowedError') || name === 'NotAllowedError' || msg.includes('Permission denied'))
    return 'Camera access denied. Please allow camera in your browser settings and try again.';
  if (msg.includes('NotFoundError') || name === 'NotFoundError')
    return 'No camera found. Try "Upload Image" or "Manual Entry" instead.';
  if (msg.includes('NotReadableError') || name === 'NotReadableError')
    return 'Camera is in use by another app. Close other apps using the camera or try Upload Image.';
  if (msg.includes('OverconstrainedError') || name === 'OverconstrainedError')
    return 'Camera does not support required settings. Try Upload Image or Manual Entry.';
  if (msg.includes('SecurityError') || name === 'SecurityError' || msg.includes('secure context'))
    return 'Camera requires HTTPS or localhost. Open this page via https:// or use Upload Image / Manual Entry.';
  if (msg.includes('AbortError') || name === 'AbortError')
    return 'Camera access was cancelled.';
  return msg || 'Failed to start camera. Try Upload Image or Manual Entry.';
}

type ScanMode = 'camera' | 'upload' | 'manual';

const ALL_MODES: ScanMode[] = ['camera', 'upload', 'manual'];

export const QRScanner: React.FC<QRScannerProps> = ({
  onScanSuccess,
  onScanError,
  width: _width = 500,
  height: _height = 500,
  labels: labelsProp,
  modes: modesProp,
}) => {
  const labels = { ...DEFAULT_LABELS, ...labelsProp };
  const modes = modesProp && modesProp.length > 0 ? modesProp : ALL_MODES;
  const defaultMode = modes[0];
  const [scanMode, setScanMode] = useState<ScanMode>(defaultMode);
  const [scanning, setScanning] = useState(false);

  // Keep scanMode in sync when parent restricts modes
  useEffect(() => {
    if (!modes.includes(scanMode)) {
      setScanMode(modes[0]);
    }
  }, [modes, scanMode]);
  const [manualInput, setManualInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [scanFrameBusy, setScanFrameBusy] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<QrScanner | null>(null);
  const noQrCountRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastDecodedRef = useRef<string | null>(null);
  const consecutiveCountRef = useRef(0);
  const scanStartTimeRef = useRef<number>(0);
  const warmupIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const REQUIRED_CONSECUTIVE = 3;
  const WARMUP_MS = 800;

  const cleanupScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.destroy();
      scannerRef.current = null;
    }
  };

  useEffect(() => {
    return () => cleanupScanner();
  }, []);

  useEffect(() => {
    if (scanMode !== 'camera' || !scanning) {
      setCameraReady(false);
      cleanupScanner();
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    lastDecodedRef.current = null;
    consecutiveCountRef.current = 0;
    noQrCountRef.current = 0;
    scanStartTimeRef.current = Date.now();
    setCameraReady(false);

    // Higher resolution (800px) helps small fiscal QRs on mobile. Same center 2/3 region as default.
    const calculateScanRegion = (v: HTMLVideoElement) => {
      const size = Math.round((2 / 3) * Math.min(v.videoWidth, v.videoHeight));
      return {
        x: Math.round((v.videoWidth - size) / 2),
        y: Math.round((v.videoHeight - size) / 2),
        width: size,
        height: size,
        downScaledWidth: 800,
        downScaledHeight: 800,
      };
    };
    const scanner = new QrScanner(
      video,
      (result) => {
        const data = typeof result === 'string' ? result : result.data;
        const now = Date.now();
        if (now - scanStartTimeRef.current < WARMUP_MS) return;
        if (data !== lastDecodedRef.current) {
          lastDecodedRef.current = data;
          consecutiveCountRef.current = 1;
          return;
        }
        consecutiveCountRef.current += 1;
        if (consecutiveCountRef.current < REQUIRED_CONSECUTIVE) return;
        setError(null);
        onScanSuccess(data);
        cleanupScanner();
        setScanning(false);
      },
      {
        returnDetailedScanResult: true,
        preferredCamera: 'environment',
        maxScansPerSecond: 25,
        highlightCodeOutline: true,
        calculateScanRegion,
        onDecodeError: (err) => {
          const msg = err instanceof Error ? err.message : String(err);
          const isNoQr = !msg || msg === 'No QR code found' || /no qr|not found/i.test(msg);
          if (isNoQr) noQrCountRef.current += 1;
        },
      }
    );

    scannerRef.current = scanner;

    scanner
      .start()
      .then(async () => {
        setError(null);
        scanStartTimeRef.current = Date.now();
        setCameraReady(true);
        scanner.setInversionMode('both');

        warmupIntervalRef.current = setInterval(() => {
          const elapsed = Date.now() - scanStartTimeRef.current;
          if (elapsed >= WARMUP_MS && warmupIntervalRef.current) {
            clearInterval(warmupIntervalRef.current);
            warmupIntervalRef.current = null;
          }
        }, 100);
      })
      .catch((err) => {
        const friendlyMsg = getCameraErrorMessage(err);
        setError(friendlyMsg);
        if (onScanError) onScanError(friendlyMsg);
        cleanupScanner();
        setScanning(false);
      });

    return () => {
      if (warmupIntervalRef.current) {
        clearInterval(warmupIntervalRef.current);
        warmupIntervalRef.current = null;
      }
      cleanupScanner();
    };
  }, [scanMode, scanning, onScanSuccess, onScanError]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);

    try {
      const result = await QrScanner.scanImage(file, { returnDetailedScanResult: true });
      const data = typeof result === 'string' ? result : result.data;
      onScanSuccess(data);
    } catch {
      const jsQRData = await tryJsQRFromFile(file);
      if (jsQRData) {
        onScanSuccess(jsQRData);
      } else {
        setError(labels.no_qr_in_image);
        if (onScanError) {
          onScanError(labels.no_qr_in_image);
        }
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!manualInput.trim()) {
      setError(labels.enter_qr_data);
      return;
    }

    setError(null);
    onScanSuccess(manualInput.trim());
    setManualInput('');
  };

  const startCameraScanning = () => {
    setScanMode('camera');
    setScanning(true);
    setError(null);
  };

  const stopScanning = () => {
    setScanning(false);
    cleanupScanner();
  };

  /** Decode QR from canvas using jsQR, with optional scale and grayscale. */
  const tryJsQROnCanvas = (
    canvas: HTMLCanvasElement,
    options?: { scale?: number; grayscale?: boolean }
  ): string | null => {
    const w = canvas.width;
    const h = canvas.height;
    if (w <= 0 || h <= 0) return null;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return null;
    let imageData = ctx.getImageData(0, 0, w, h);
    let data = imageData.data;
    let width = w;
    let height = h;

    if (options?.scale && options.scale !== 1) {
      const scale = options.scale;
      const c2 = document.createElement('canvas');
      c2.width = w * scale;
      c2.height = h * scale;
      const ctx2 = c2.getContext('2d', { alpha: false });
      if (!ctx2) return null;
      ctx2.drawImage(canvas, 0, 0, w, h, 0, 0, w * scale, h * scale);
      imageData = ctx2.getImageData(0, 0, c2.width, c2.height);
      data = imageData.data;
      width = c2.width;
      height = c2.height;
    }

    if (options?.grayscale) {
      const out = new Uint8ClampedArray(data.length);
      for (let i = 0; i < data.length; i += 4) {
        const y = (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) | 0;
        out[i] = out[i + 1] = out[i + 2] = y;
        out[i + 3] = 255;
      }
      data = out;
    }

    const code = jsQR(data, width, height, { inversionAttempts: 'attemptBoth' });
    return code ? code.data : null;
  };

  /** Try jsQR with multiple strategies: 1x, 1.5x, 2x upscale, grayscale, grayscale+2x. */
  const tryJsQRAllStrategies = (canvas: HTMLCanvasElement): string | null => {
    const strategies: { scale?: number; grayscale?: boolean }[] = [
      {},
      { scale: 1.5 },
      { scale: 2 },
      { grayscale: true },
      { scale: 2, grayscale: true },
    ];
    for (const opts of strategies) {
      const result = tryJsQROnCanvas(canvas, opts);
      if (result) return result;
    }
    return null;
  };

  /** Load file as image, draw to canvas, run jsQR (all strategies). Used when QrScanner.scanImage fails on file. */
  const tryJsQRFromFile = (file: File): Promise<string | null> =>
    new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d', { alpha: false });
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(tryJsQRAllStrategies(canvas));
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });

  /** Scan current video frame: full-resolution canvas first, then qr-scanner, then jsQR fallback. */
  const handleScanCurrentFrame = async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;
    setScanFrameBusy(true);
    setError(null);
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (w <= 0 || h <= 0) {
      setScanFrameBusy(false);
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) {
      setScanFrameBusy(false);
      return;
    }
    ctx.drawImage(video, 0, 0, w, h, 0, 0, w, h);

    // Try jsQR first with multiple strategies (1x, 2x, grayscale, grayscale+2x) — works better for small/low-contrast fiscal QRs on some devices
    let data: string | null = tryJsQRAllStrategies(canvas);
    if (data) {
      setError(null);
      onScanSuccess(data);
      cleanupScanner();
      setScanning(false);
      setScanFrameBusy(false);
      return;
    }

    try {
      const result = await QrScanner.scanImage(canvas, {
        alsoTryWithoutScanRegion: true,
        returnDetailedScanResult: true,
      });
      data = typeof result === 'string' ? result : result.data;
      setError(null);
      onScanSuccess(data);
      cleanupScanner();
      setScanning(false);
    } catch {
      data = tryJsQRAllStrategies(canvas);
      if (data) {
        setError(null);
        onScanSuccess(data);
        cleanupScanner();
        setScanning(false);
      } else {
        setError(labels.no_qr_in_image);
      }
    } finally {
      setScanFrameBusy(false);
    }
  };

  const showModeSelector = modes.length > 1;

  return (
    <div className="qr-scanner-wrapper">
      {showModeSelector && (
        <div className="scan-mode-selector">
          {modes.includes('camera') && (
            <button
              className={`mode-btn ${scanMode === 'camera' ? 'active' : ''}`}
              onClick={() => setScanMode('camera')}
              disabled={scanning}
            >
              📷 {labels.camera}
            </button>
          )}
          {modes.includes('upload') && (
            <button
              className={`mode-btn ${scanMode === 'upload' ? 'active' : ''}`}
              onClick={() => setScanMode('upload')}
              disabled={scanning}
            >
              📁 {labels.upload_image}
            </button>
          )}
          {modes.includes('manual') && (
            <button
              className={`mode-btn ${scanMode === 'manual' ? 'active' : ''}`}
              onClick={() => setScanMode('manual')}
              disabled={scanning}
            >
              ⌨️ {labels.manual_entry}
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      {scanMode === 'camera' && (
        <div className="camera-scanner-container">
          {!scanning ? (
            <button
              className="start-scan-btn"
              onClick={startCameraScanning}
            >
              {labels.start_camera}
            </button>
          ) : (
            <>
              <div className="qr-scanner-viewfinder" style={{ width: '100%', minHeight: 300 }}>
                <video
                  ref={videoRef}
                  playsInline
                  muted
                  style={{ width: '100%', maxHeight: 400, objectFit: 'cover' }}
                  aria-label="Camera for QR scanning"
                />
              </div>
              <p className="camera-hint">
                {labels.camera_hint}
              </p>
              <button
                className="stop-scan-btn"
                onClick={stopScanning}
              >
                {labels.stop_scanning}
              </button>
              {cameraReady && (
                <button
                  type="button"
                  className="scan-frame-btn"
                  onClick={handleScanCurrentFrame}
                  disabled={scanFrameBusy}
                >
                  {scanFrameBusy ? labels.processing : labels.scan_current_frame}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {scanMode === 'upload' && (
        <div className="upload-container">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="file-input"
          />
          <p className="upload-hint">
            {labels.upload_hint}
          </p>
        </div>
      )}

      {scanMode === 'manual' && (
        <div className="manual-entry-container">
          <form onSubmit={handleManualSubmit}>
            <textarea
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              placeholder={labels.manual_placeholder}
              rows={6}
              className="manual-input"
            />
            <button type="submit" className="submit-btn">
              {labels.process_qr_data}
            </button>
          </form>
          <p className="manual-hint">
            {labels.manual_hint_example}
            <br />
            JSON: {`{"iic": "...", "tin": "...", ...}`}
            <br />
            Pipe: IIC|TIN|DateTime|InvoiceNum|Amount
          </p>
        </div>
      )}

      <style>{`
        .qr-scanner-viewfinder {
          min-height: 300px;
          position: relative;
          overflow: hidden;
          border-radius: 8px;
          background: #000;
        }
        .qr-scanner-viewfinder video {
          width: 100% !important;
          max-height: 400px;
          object-fit: cover;
        }
        .qr-scanner-wrapper {
          width: 100%;
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
          border: 1px solid #ddd;
          border-radius: 8px;
          background-color: #fff;
        }

        .scan-mode-selector {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
        }

        .mode-btn {
          flex: 1;
          padding: 12px;
          border: 2px solid #ddd;
          border-radius: 6px;
          background-color: #fff;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s;
        }

        .mode-btn:hover:not(:disabled) {
          border-color: #007bff;
          background-color: #f0f8ff;
        }

        .mode-btn.active {
          border-color: #007bff;
          background-color: #007bff;
          color: white;
        }

        .mode-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .error-message {
          padding: 12px;
          margin-bottom: 20px;
          background-color: #fee;
          border: 1px solid #fcc;
          border-radius: 6px;
          color: #c33;
        }

        .camera-scanner-container {
          text-align: center;
        }

        .start-scan-btn,
        .stop-scan-btn,
        .submit-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 6px;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.3s;
        }

        .start-scan-btn {
          background-color: #28a745;
          color: white;
        }

        .start-scan-btn:hover {
          background-color: #218838;
        }

        .camera-hint {
          margin: 10px 0 0;
          font-size: 13px;
          color: #666;
        }

        .stop-scan-btn {
          background-color: #dc3545;
          color: white;
          margin-top: 10px;
        }

        .stop-scan-btn:hover {
          background-color: #c82333;
        }

        .scan-frame-btn {
          display: block;
          width: 100%;
          margin-top: 12px;
          padding: 12px;
          border: 2px solid #17a2b8;
          border-radius: 6px;
          background: #fff;
          color: #17a2b8;
          font-size: 15px;
          cursor: pointer;
        }
        .scan-frame-btn:hover:not(:disabled) {
          background: #17a2b8;
          color: white;
        }
        .scan-frame-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .upload-container {
          text-align: center;
          padding: 40px 20px;
          border: 2px dashed #ddd;
          border-radius: 8px;
        }

        .file-input {
          display: block;
          margin: 20px auto;
          padding: 10px;
        }

        .upload-hint {
          color: #666;
          font-size: 14px;
        }

        .manual-entry-container {
          padding: 20px;
        }

        .manual-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-family: monospace;
          font-size: 14px;
          resize: vertical;
        }

        .submit-btn {
          width: 100%;
          margin-top: 15px;
          background-color: #007bff;
          color: white;
        }

        .submit-btn:hover {
          background-color: #0056b3;
        }

        .manual-hint {
          margin-top: 15px;
          padding: 10px;
          background-color: #f8f9fa;
          border-radius: 4px;
          font-size: 12px;
          color: #666;
          font-family: monospace;
        }
      `}</style>
    </div>
  );
};

export default QRScanner;
